import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import DatePicker from "react-datepicker"; // Import the new date picker
import "react-datepicker/dist/react-datepicker.css"; // Import the styles
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import { toast } from "react-toastify";
import moment from "moment";
import { useAuth } from "../../Common/Configurations/AuthContext";
import countriesData from "../../Data/Countries.json";
import {
  RiUserLine,
  RiPassportLine,
  RiUploadCloudLine,
  RiCloseLine,
  RiFileTextLine,
  RiEyeLine,
  RiFilePdf2Line,
  RiEditLine,
  RiAddLine,
  RiDeleteBinLine,
  RiCalendarLine,
} from "react-icons/ri";

const DEFAULT_PROFILE = "/assets/images/avatar.png";
const DEFAULT_DOCUMENT = "/assets/images/doc_bgc.png";

// Function to calculate passport expiry (10 years from today)
const calculateDefaultPassportExpiry = () => {
  const expiryDate = new Date();
  expiryDate.setFullYear(expiryDate.getFullYear() + 10);
  expiryDate.setDate(expiryDate.getDate() - 1); // Subtract one day to make it exactly 10 years
  return expiryDate;
};

const initialFieldValues = {
  visaApplicantId: "00000000-0000-0000-0000-000000000000",
  visaRequestId: "",
  firstName: "",
  lastName: "",
  title: "",
  gender: "Male",
  dateOfBirth: new Date(),
  nationality: "India",
  passportNumber: "",
  passportCountry: "India",
  passportIssueDate: new Date(),
  passportExpireDate: calculateDefaultPassportExpiry(), // Set default expiry to 10 years from today
  travelerType: "Adult",
  visaApplicantStatus: "PENDING",
  comments: "",
  profileImageFile: null,
  passportFrontFile: null,
  passportBackFile: null,
  additionalDocuments: [],
};

const ApplicantForm = ({
  show,
  onHide,
  visaRequestId,
  applicantForEdit,
  onSuccess,
  applicantsList = [],
}) => {
  const { user } = useAuth();
  const [values, setValues] = useState(initialFieldValues);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [previews, setPreviews] = useState({
    profileSrc: DEFAULT_PROFILE,
    passportFrontSrc: DEFAULT_DOCUMENT,
    passportBackSrc: DEFAULT_DOCUMENT,
  });
  const [previewTypes, setPreviewTypes] = useState({
    profileSrc: "image",
    passportFrontSrc: "image",
    passportBackSrc: "image",
  });
  const [existingAdditionalDocs, setExistingAdditionalDocs] = useState([]);
  const [newAdditionalDocs, setNewAdditionalDocs] = useState([]);
  const [newAdditionalDocPreviews, setNewAdditionalDocPreviews] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [fileErrors, setFileErrors] = useState({
    profileImageFile: false,
    passportFrontFile: false,
    passportBackFile: false,
  });
  const [showValidation, setShowValidation] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [applicantToDelete, setApplicantToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showProfilePreview, setShowProfilePreview] = useState(false);
  const [showPassportFrontPreview, setShowPassportFrontPreview] =
    useState(false);
  const [showPassportBackPreview, setShowPassportBackPreview] = useState(false);

  useEffect(() => {
    if (show) {
      setRecordForEdit(applicantForEdit);
      if (applicantForEdit) {
        setValues({
          ...initialFieldValues,
          ...applicantForEdit,
          visaRequestId: visaRequestId,
          dateOfBirth: new Date(applicantForEdit.dateOfBirth),
          passportIssueDate: new Date(applicantForEdit.passportIssueDate),
          passportExpireDate: new Date(applicantForEdit.passportExpireDate),
          profileImageFile: null,
          passportFrontFile: null,
          passportBackFile: null,
          additionalDocuments: [],
        });

        const profileType = applicantForEdit.profileSrc?.includes?.("pdf")
          ? "pdf"
          : "image";
        const passportFrontType = applicantForEdit.passportFrontSrc?.includes?.(
          "pdf"
        )
          ? "pdf"
          : "image";
        const passportBackType = applicantForEdit.passportBackSrc?.includes?.(
          "pdf"
        )
          ? "pdf"
          : "image";

        setPreviews({
          profileSrc: applicantForEdit.profileSrc || DEFAULT_PROFILE,
          passportFrontSrc:
            applicantForEdit.passportFrontSrc || DEFAULT_DOCUMENT,
          passportBackSrc: applicantForEdit.passportBackSrc || DEFAULT_DOCUMENT,
        });

        setPreviewTypes({
          profileSrc: profileType,
          passportFrontSrc: passportFrontType,
          passportBackSrc: passportBackType,
        });

        if (
          applicantForEdit.documents &&
          applicantForEdit.documents.length > 0
        ) {
          setExistingAdditionalDocs(applicantForEdit.documents);
        }

        setFileErrors({
          profileImageFile: false,
          passportFrontFile: false,
          passportBackFile: false,
        });
        setShowValidation(false);
      } else {
        resetForm();
      }
    }
  }, [applicantForEdit, visaRequestId, show]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });

    if (value.trim() !== "" && errors[name] === false) {
      setErrors({ ...errors, [name]: false });
    }
  };
  const handleNationalityChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
      // Auto-fill passport country with the same value as nationality
      passportCountry: value,
    });

    if (value.trim() !== "" && errors[name] === false) {
      setErrors({ ...errors, [name]: false });
    }
  };

  // Update the handlePassportIssueDateChange function
  const handlePassportIssueDateChange = (date) => {
    const issueDate = date;
    const expiryDate = calculatePassportExpiry(issueDate);

    setValues({
      ...values,
      passportIssueDate: issueDate,
      passportExpireDate: expiryDate,
    });
  };

  const handleFileChange = (e, fileField, previewField) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }

      if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
        toast.error("Please upload an image or PDF file");
        return;
      }

      setValues({ ...values, [fileField]: file });

      if (fileErrors[fileField]) {
        setFileErrors({ ...fileErrors, [fileField]: false });
      }

      const fileType = file.type === "application/pdf" ? "pdf" : "image";
      setPreviewTypes((prev) => ({ ...prev, [previewField]: fileType }));

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews((prev) => ({ ...prev, [previewField]: reader.result }));
        };
        reader.readAsDataURL(file);
      } else {
        setPreviews((prev) => ({ ...prev, [previewField]: DEFAULT_DOCUMENT }));
      }
    }
  };

  const handleAdditionalFilesChange = (e) => {
    const files = Array.from(e.target.files);

    const validFiles = files.filter((file) => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large (max 5MB)`);
        return false;
      }
      if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
        toast.error(`File ${file.name} is not an image or PDF`);
        return false;
      }
      return true;
    });

    const newPreviews = [];
    const previewPromises = validFiles.map((file) => {
      return new Promise((resolve) => {
        const fileType = file.type === "application/pdf" ? "pdf" : "image";
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              type: fileType,
              preview: reader.result,
              name: file.name,
            });
          };
          reader.readAsDataURL(file);
        } else {
          resolve({
            type: fileType,
            preview: DEFAULT_DOCUMENT,
            name: file.name,
          });
        }
      });
    });

    Promise.all(previewPromises).then((previewResults) => {
      setNewAdditionalDocPreviews((prev) => [...prev, ...previewResults]);
    });

    setNewAdditionalDocs((prev) => [...prev, ...validFiles]);
  };

  const removeNewAdditionalFile = (index) => {
    const newFiles = [...newAdditionalDocs];
    newFiles.splice(index, 1);
    setNewAdditionalDocs(newFiles);

    const newPreviews = [...newAdditionalDocPreviews];
    newPreviews.splice(index, 1);
    setNewAdditionalDocPreviews(newPreviews);
  };

  const removeExistingAdditionalFile = async (docId, index) => {
    try {
      const res = await apiService.remove(
        config.DELETEVISAAPPLICANTDOCUMENT,
        docId
      );

      if (res) {
        const newFiles = [...existingAdditionalDocs];
        newFiles.splice(index, 1);
        setExistingAdditionalDocs(newFiles);
      }
    } catch (error) {}
  };
  // Add this function to calculate passport expiry
  const calculatePassportExpiry = (issueDate) => {
    if (!issueDate) return calculateDefaultPassportExpiry();

    const expiryDate = new Date(issueDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + 10);
    expiryDate.setDate(expiryDate.getDate() - 1);
    return expiryDate;
  };
  const validate = () => {
    let temp = {};
    temp.firstName = values.firstName.trim() !== "";
    temp.lastName = values.lastName.trim() !== "";
    temp.passportNumber = values.passportNumber.trim() !== "";
    temp.passportIssueDate = values.passportIssueDate instanceof Date;
    temp.passportExpireDate = values.passportExpireDate instanceof Date;
    temp.dateOfBirth = values.dateOfBirth instanceof Date;

    setErrors(temp);

    let fileTemp = { ...fileErrors };

    if (!recordForEdit) {
      fileTemp.profileImageFile = values.profileImageFile !== null;
      fileTemp.passportFrontFile = values.passportFrontFile !== null;
      fileTemp.passportBackFile = values.passportBackFile !== null;
    } else {
      fileTemp.profileImageFile = true;
      fileTemp.passportFrontFile = true;
      fileTemp.passportBackFile = true;
    }

    setFileErrors(fileTemp);

    return (
      Object.values(temp).every((x) => x === true) &&
      Object.values(fileTemp).every((x) => x === true)
    );
  };

  const resetForm = () => {
    setValues({
      ...initialFieldValues,
      visaRequestId,
      passportExpireDate: calculateDefaultPassportExpiry(), // Reset to default expiry when form resets
    });
    setPreviews({
      profileSrc: DEFAULT_PROFILE,
      passportFrontSrc: DEFAULT_DOCUMENT,
      passportBackSrc: DEFAULT_DOCUMENT,
    });
    setPreviewTypes({
      profileSrc: "image",
      passportFrontSrc: "image",
      passportBackSrc: "image",
    });
    setExistingAdditionalDocs([]);
    setNewAdditionalDocs([]);
    setNewAdditionalDocPreviews([]);
    setBtnLoading(false);
    setRecordForEdit(null);
    setErrors({});
    setFileErrors({
      profileImageFile: false,
      passportFrontFile: false,
      passportBackFile: false,
    });
    setShowValidation(false);
    setSelectedApplicant(null);
    setShowProfilePreview(false);
    setShowPassportFrontPreview(false);
    setShowPassportBackPreview(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (validate()) {
      setBtnLoading(true);

      const formData = new FormData();

      formData.append("visaApplicantId", values.visaApplicantId);
      formData.append("visaRequestId", visaRequestId);
      formData.append("firstName", values.firstName);
      formData.append("lastName", values.lastName);
      formData.append("title", values.title | "");
      formData.append("gender", values.gender);
      formData.append(
        "dateOfBirth",
        moment(values.dateOfBirth).format("YYYY-MM-DD")
      );
      formData.append("nationality", values.nationality);
      formData.append("passportNumber", values.passportNumber);
      formData.append("passportCountry", values.passportCountry);
      formData.append("visaApplicantStatus", values.visaApplicantStatus);
      formData.append(
        "comments",
        values.comments ? values.comments.trim() : " "
      );
      formData.append(
        "passportIssueDate",
        moment(values.passportIssueDate).format("YYYY-MM-DD")
      );
      formData.append(
        "passportExpireDate",
        moment(values.passportExpireDate).format("YYYY-MM-DD")
      );
      formData.append("travelerType", values.travelerType);

      if (values.profileImageFile) {
        formData.append("profileImageFile", values.profileImageFile);
      }
      if (values.passportFrontFile) {
        formData.append("passportFrontFile", values.passportFrontFile);
      }
      if (values.passportBackFile) {
        formData.append("passportBackFile", values.passportBackFile);
      }

      newAdditionalDocs.forEach((file) => {
        formData.append("additionalDocuments", file);
      });

      existingAdditionalDocs.forEach((doc) => {
        formData.append(
          "existingAdditionalDocumentIds",
          doc.visaApplicantDocumentId
        );
      });

      try {
        const isNew =
          values.visaApplicantId === "00000000-0000-0000-0000-000000000000";

        if (isNew) {
          await apiService.create(config.CREATEVISAAPPLICANT, formData);
        } else {
          await apiService.update(config.UPDATEVISAAPPLICANT, formData);
        }

        onSuccess();
        resetForm();
      } catch (error) {
        console.error("Error saving applicant:", error);
      } finally {
        setBtnLoading(false);
      }
    }
  };

  // Delete applicant function
  const handleDeleteApplicant = async () => {
    if (!applicantToDelete) return;

    setDeleteLoading(true);
    try {
      const res = await apiService.remove(
        config.DELETEVISAAPPLICANT,
        applicantToDelete.visaApplicantId
      );

      if (res) {
        onSuccess();
        if (
          selectedApplicant?.visaApplicantId ===
          applicantToDelete.visaApplicantId
        ) {
          resetForm();
        }
      } else {
      }
    } catch (error) {
      console.error("Error deleting applicant:", error);
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
      setApplicantToDelete(null);
    }
  };

  const showDeleteConfirmation = (applicant) => {
    setApplicantToDelete(applicant);
    setShowDeleteConfirm(true);
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false && showValidation
      ? "is-invalid"
      : "";

  const applyFileErrorClass = (field) =>
    fileErrors[field] === false && showValidation ? "is-invalid" : "";

  const renderDocumentIcon = (doc) => {
    if (
      doc.type === "pdf" ||
      doc.imageName?.endsWith?.(".pdf") ||
      doc.imageSrc?.includes?.("pdf")
    ) {
      return <RiFilePdf2Line className="text-danger me-1" />;
    }
    return <RiFileTextLine className="text-primary me-1" />;
  };

  const getDocumentType = (file) => {
    if (file.type === "application/pdf") return "pdf";
    if (file.type.startsWith("image/")) return "image";
    return "file";
  };

  const renderDocumentPreview = (src, type, alt, className = "") => {
    return (
      <div className="position-relative">
        {type === "pdf" ? (
          <div className="text-center">
            <div
              className={`${className} d-flex align-items-center justify-content-center bg-light border`}
              style={{
                height: "120px",
                width: "100%",
                backgroundColor: "#f8f9fa",
              }}
            >
              <RiFilePdf2Line className="text-danger" size={48} />
            </div>
            <div className="mt-2">
              <RiFilePdf2Line className="text-danger me-1" size={16} />
              <small className="text-muted">PDF Document</small>
            </div>
          </div>
        ) : (
          <img
            src={src}
            alt={alt}
            className={className}
            style={{ height: "120px", objectFit: "cover" }}
          />
        )}
      </div>
    );
  };

  const renderAdditionalDocPreview = (preview, type, alt) => {
    if (type === "pdf") {
      return (
        <div className="document-preview mb-2">
          <div
            className="img-thumbnail d-flex align-items-center justify-content-center bg-light"
            style={{ height: "80px", objectFit: "cover" }}
          >
            <RiFilePdf2Line className="text-danger" size={32} />
          </div>
        </div>
      );
    }
    return (
      <div className="document-preview mb-2">
        <img
          src={preview}
          alt={alt}
          className="img-thumbnail"
          style={{ height: "80px", objectFit: "cover" }}
        />
      </div>
    );
  };

  const handleEditApplicant = (applicant) => {
    setSelectedApplicant(applicant);
    setRecordForEdit(applicant);
    setValues({
      ...initialFieldValues,
      ...applicant,
      visaRequestId: visaRequestId,
      dateOfBirth: new Date(applicant.dateOfBirth),
      passportIssueDate: new Date(applicant.passportIssueDate),
      passportExpireDate: new Date(applicant.passportExpireDate),
      profileImageFile: null,
      passportFrontFile: null,
      passportBackFile: null,
      additionalDocuments: [],
    });

    const profileType = applicant.profileSrc?.includes?.("pdf")
      ? "pdf"
      : "image";
    const passportFrontType = applicant.passportFrontSrc?.includes?.("pdf")
      ? "pdf"
      : "image";
    const passportBackType = applicant.passportBackSrc?.includes?.("pdf")
      ? "pdf"
      : "image";

    setPreviews({
      profileSrc: applicant.profileSrc || DEFAULT_PROFILE,
      passportFrontSrc: applicant.passportFrontSrc || DEFAULT_DOCUMENT,
      passportBackSrc: applicant.passportBackSrc || DEFAULT_DOCUMENT,
    });

    setPreviewTypes({
      profileSrc: profileType,
      passportFrontSrc: passportFrontType,
      passportBackSrc: passportBackType,
    });

    if (applicant.documents && applicant.documents.length > 0) {
      setExistingAdditionalDocs(applicant.documents);
    }
  };

  const handleAddNewApplicant = () => {
    setSelectedApplicant(null);
    setRecordForEdit(null);
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  // Custom input component for date picker
  const CustomDateInput = React.forwardRef(({ value, onClick, error }, ref) => (
    <div className="input-group">
      <input
        type="text"
        className={`form-control ${error ? "is-invalid" : ""}`}
        value={value}
        onClick={onClick}
        ref={ref}
        readOnly
        placeholder="Select date"
      />
      <button className="btn btn-secondary" type="button" onClick={onClick}>
        <RiCalendarLine />
      </button>
    </div>
  ));

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            {recordForEdit ? "Edit Applicant" : "Add New Applicant"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <div className="row">
            {/* Applicants List - Left Side */}
            <div className="col-md-3 border-end">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Applicants</h6>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddNewApplicant}
                  className="d-flex align-items-center"
                >
                  <RiAddLine className="me-1" /> Add
                </Button>
              </div>
              <div className="applicants-list">
                {applicantsList && applicantsList.length > 0 ? (
                  applicantsList.map((applicant, index) => (
                    <div
                      key={applicant.visaApplicantId}
                      className={`applicant-item p-2 mb-2 rounded ${
                        selectedApplicant?.visaApplicantId ===
                        applicant.visaApplicantId
                          ? "bg-primary text-white"
                          : "bg-light"
                      }`}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-semibold">
                          {applicant.firstName} {applicant.lastName}
                        </span>
                        <div className="d-flex gap-1">
                          <RiEditLine
                            size={14}
                            className="cursor-pointer"
                            onClick={() => handleEditApplicant(applicant)}
                            title="Edit applicant"
                          />
                          <RiDeleteBinLine
                            size={14}
                            className="cursor-pointer text-danger"
                            onClick={() => showDeleteConfirmation(applicant)}
                            title="Delete applicant"
                          />
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="d-block">
                          {applicant.passportNumber}
                        </small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-3">
                    No applicants added yet
                  </div>
                )}
              </div>
            </div>

            {/* Applicant Form - Right Side */}
            <div className="col-md-9">
              <form
                onSubmit={handleSubmit}
                noValidate
                encType="multipart/form-data"
              >
                {/* Document Upload Section */}
                <div className="card mb-4 shadow-sm">
                  <div className="card-header bg-primary">
                    <h5 className="mb-0 text-white">
                      <RiUploadCloudLine className="me-2" />
                      Document Uploads
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4 mb-4 text-center">
                        <label className="form-label d-block">
                          Profile Photo
                          {!recordForEdit && " *"}
                        </label>
                        {renderDocumentPreview(
                          previews.profileSrc,
                          previewTypes.profileSrc,
                          "Profile",
                          "img-thumbnail mb-3"
                        )}
                        <div className="d-flex gap-2 mb-2">
                          <input
                            type="file"
                            id="profileImageFile"
                            className="d-none"
                            accept="image/*,.pdf"
                            onChange={(e) =>
                              handleFileChange(
                                e,
                                "profileImageFile",
                                "profileSrc"
                              )
                            }
                          />
                          <label
                            htmlFor="profileImageFile"
                            className="btn btn-sm btn-outline-primary flex-fill"
                          >
                            <RiUploadCloudLine className="me-1" />
                            Upload
                          </label>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowProfilePreview(true)}
                            disabled={
                              !values.profileImageFile && !recordForEdit
                            }
                          >
                            <RiEyeLine className="me-1" />
                            View
                          </Button>
                        </div>
                        <small className="text-muted">
                          Max 5MB, JPG/PNG/PDF
                        </small>
                        {fileErrors.profileImageFile === false &&
                          showValidation && (
                            <div className="invalid-feedback d-block">
                              Profile photo is required
                            </div>
                          )}
                      </div>

                      <div className="col-md-4 mb-4 text-center">
                        <label className="form-label d-block">
                          Passport Front Page{!recordForEdit && " *"}
                        </label>
                        {renderDocumentPreview(
                          previews.passportFrontSrc,
                          previewTypes.passportFrontSrc,
                          "Passport Front",
                          "img-thumbnail mb-3"
                        )}
                        <div className="d-flex gap-2 mb-2">
                          <input
                            type="file"
                            id="passportFrontFile"
                            className="d-none"
                            accept="image/*,.pdf"
                            onChange={(e) =>
                              handleFileChange(
                                e,
                                "passportFrontFile",
                                "passportFrontSrc"
                              )
                            }
                          />
                          <label
                            htmlFor="passportFrontFile"
                            className="btn btn-sm btn-outline-primary flex-fill"
                          >
                            <RiUploadCloudLine className="me-1" />
                            Upload
                          </label>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowPassportFrontPreview(true)}
                            disabled={
                              !values.passportFrontFile && !recordForEdit
                            }
                          >
                            <RiEyeLine className="me-1" />
                            View
                          </Button>
                        </div>
                        <small className="text-muted">
                          Max 5MB, JPG/PNG/PDF
                        </small>
                        {fileErrors.passportFrontFile === false &&
                          showValidation && (
                            <div className="invalid-feedback d-block">
                              Passport front page is required
                            </div>
                          )}
                      </div>

                      <div className="col-md-4 mb-4 text-center">
                        <label className="form-label d-block">
                          Passport Back Page{!recordForEdit && " *"}
                        </label>
                        {renderDocumentPreview(
                          previews.passportBackSrc,
                          previewTypes.passportBackSrc,
                          "Passport Back",
                          "img-thumbnail mb-3"
                        )}
                        <div className="d-flex gap-2 mb-2">
                          <input
                            type="file"
                            id="passportBackFile"
                            className="d-none"
                            accept="image/*,.pdf"
                            onChange={(e) =>
                              handleFileChange(
                                e,
                                "passportBackFile",
                                "passportBackSrc"
                              )
                            }
                          />
                          <label
                            htmlFor="passportBackFile"
                            className="btn btn-sm btn-outline-primary flex-fill"
                          >
                            <RiUploadCloudLine className="me-1" />
                            Upload
                          </label>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowPassportBackPreview(true)}
                            disabled={
                              !values.passportBackFile && !recordForEdit
                            }
                          >
                            <RiEyeLine className="me-1" />
                            View
                          </Button>
                        </div>
                        <small className="text-muted">
                          Max 5MB, JPG/PNG/PDF
                        </small>
                        {fileErrors.passportBackFile === false &&
                          showValidation && (
                            <div className="invalid-feedback d-block">
                              Passport back page is required
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  {/* Mandatory Information Section */}
                  <div className="col-md-6">
                    <div className="card mb-4 shadow-sm">
                      <div className="card-header bg-primary">
                        <h5 className="mb-0 text-white">
                          <RiUserLine className="me-2" />
                          Mandatory Information
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">First Name *</label>
                            <input
                              type="text"
                              name="firstName"
                              value={values.firstName}
                              onChange={handleInputChange}
                              className={`form-control ${applyErrorClass(
                                "firstName"
                              )}`}
                              placeholder="Enter first name"
                              maxLength={45}
                              required
                            />
                            {errors.firstName === false && showValidation && (
                              <div className="invalid-feedback d-block">
                                First name is required
                              </div>
                            )}
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">Last Name *</label>
                            <input
                              type="text"
                              name="lastName"
                              value={values.lastName}
                              onChange={handleInputChange}
                              className={`form-control ${applyErrorClass(
                                "lastName"
                              )}`}
                              placeholder="Enter last name"
                              maxLength={45}
                              required
                            />
                            {errors.lastName === false && showValidation && (
                              <div className="invalid-feedback d-block">
                                Last name is required
                              </div>
                            )}
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Passport Number *
                            </label>
                            <input
                              type="text"
                              name="passportNumber"
                              value={values.passportNumber}
                              onChange={handleInputChange}
                              className={`form-control ${applyErrorClass(
                                "passportNumber"
                              )}`}
                              placeholder="Enter passport number"
                              maxLength={15}
                              required
                            />
                            {errors.passportNumber === false &&
                              showValidation && (
                                <div className="invalid-feedback d-block">
                                  Passport number is required
                                </div>
                              )}
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Date of Birth *
                            </label>
                            <DatePicker
                              selected={values.dateOfBirth}
                              onChange={(date) =>
                                setValues({ ...values, dateOfBirth: date })
                              }
                              customInput={
                                <CustomDateInput
                                  error={
                                    errors.dateOfBirth === false &&
                                    showValidation
                                  }
                                />
                              }
                              dateFormat="dd-MMM-yyyy"
                              showYearDropdown
                              showMonthDropdown
                              dropdownMode="select"
                              maxDate={new Date()}
                              yearDropdownItemNumber={50}
                              scrollableYearDropdown
                              className="form-control"
                              required
                            />
                            {errors.dateOfBirth === false && showValidation && (
                              <div className="invalid-feedback d-block">
                                Date of birth is required
                              </div>
                            )}
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Passport Issue Date *
                            </label>
                            <DatePicker
                              selected={values.passportIssueDate}
                              onChange={handlePassportIssueDateChange}
                              customInput={
                                <CustomDateInput
                                  error={
                                    errors.passportIssueDate === false &&
                                    showValidation
                                  }
                                />
                              }
                              dateFormat="dd-MMM-yyyy"
                              showYearDropdown
                              showMonthDropdown
                              dropdownMode="select"
                              maxDate={new Date()}
                              yearDropdownItemNumber={50}
                              scrollableYearDropdown
                              className="form-control"
                              required
                            />
                            {errors.passportIssueDate === false &&
                              showValidation && (
                                <div className="invalid-feedback d-block">
                                  Passport issue date is required
                                </div>
                              )}
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Passport Expiry Date *
                            </label>
                            <DatePicker
                              selected={values.passportExpireDate}
                              onChange={(date) =>
                                setValues({
                                  ...values,
                                  passportExpireDate: date,
                                })
                              }
                              customInput={
                                <CustomDateInput
                                  error={
                                    errors.passportExpireDate === false &&
                                    showValidation
                                  }
                                />
                              }
                              dateFormat="dd-MMM-yyyy"
                              showYearDropdown
                              showMonthDropdown
                              dropdownMode="select"
                              minDate={new Date()}
                              yearDropdownItemNumber={50}
                              scrollableYearDropdown
                              className="form-control"
                              required
                            />
                            {errors.passportExpireDate === false &&
                              showValidation && (
                                <div className="invalid-feedback d-block">
                                  Passport expiry date is required
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Other Information Section */}
                  <div className="col-md-6">
                    <div className="card mb-4 shadow-sm">
                      <div className="card-header bg-primary">
                        <h5 className="mb-0 text-white">
                          <RiPassportLine className="me-2" />
                          Other Information
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Nationality </label>
                            <select
                              name="nationality"
                              value={values.nationality}
                              onChange={handleNationalityChange}
                              className={`form-select ${applyErrorClass(
                                "nationality"
                              )}`}
                            >
                              <option value="">Select Nationality</option>
                              {countriesData.map((country) => (
                                <option key={country.code} value={country.name}>
                                  {country.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Passport Country
                            </label>
                            <select
                              name="passportCountry"
                              value={values.passportCountry}
                              onChange={handleInputChange}
                              className={`form-select ${applyErrorClass(
                                "passportCountry"
                              )}`}
                            >
                              <option value="">Select Passport Country</option>
                              {countriesData.map((country) => (
                                <option key={country.code} value={country.name}>
                                  {country.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">Traveler Type</label>
                            <select
                              name="travelerType"
                              value={values.travelerType}
                              onChange={handleInputChange}
                              className={`form-select ${applyErrorClass(
                                "travelerType"
                              )}`}
                              required
                            >
                              <option value="Adult">Adult</option>
                              <option value="Child">Child</option>
                              <option value="Infant">Infant</option>
                            </select>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">Gender </label>
                            <select
                              name="gender"
                              value={values.gender}
                              onChange={handleInputChange}
                              className={`form-select ${applyErrorClass(
                                "gender"
                              )}`}
                            >
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Documents and Information Sections */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="card mb-4 shadow-sm h-100">
                      <div className="card-header bg-primary">
                        <h5 className="mb-0 text-white">
                          <RiUploadCloudLine className="me-2" />
                          Additional Documents
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label">Upload Documents</label>
                          <input
                            type="file"
                            className="form-control"
                            multiple
                            onChange={handleAdditionalFilesChange}
                            accept="image/*,.pdf"
                          />
                          <small className="text-muted">
                            You can upload multiple files (Max 5MB each,
                            JPG/PNG/PDF)
                          </small>
                        </div>

                        <div className="row">
                          {existingAdditionalDocs.map((doc, index) => (
                            <div
                              key={doc.visaApplicantDocumentId}
                              className="col-md-6 col-sm-6 mb-3"
                            >
                              <div className="card document-card">
                                <div className="card-body text-center p-2">
                                  {doc.imageName?.endsWith?.(".pdf") ||
                                  doc.imageSrc?.includes?.("pdf") ? (
                                    <div className="document-preview mb-2">
                                      <div
                                        className="img-thumbnail d-flex align-items-center justify-content-center bg-light"
                                        style={{
                                          height: "80px",
                                          objectFit: "cover",
                                        }}
                                      >
                                        <RiFilePdf2Line
                                          className="text-danger"
                                          size={32}
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="document-preview mb-2">
                                      <img
                                        src={doc.imageSrc || DEFAULT_DOCUMENT}
                                        alt={`Document ${index + 1}`}
                                        className="img-thumbnail"
                                        style={{
                                          height: "80px",
                                          objectFit: "cover",
                                        }}
                                      />
                                    </div>
                                  )}
                                  <h6 className="document-title mb-1">
                                    {renderDocumentIcon(doc)}
                                    {doc.imageName
                                      ? doc.imageName.substring(0, 15) +
                                        (doc.imageName.length > 15 ? "..." : "")
                                      : `Document ${index + 1}`}
                                  </h6>
                                  <div className="d-flex justify-content-center gap-2">
                                    <a
                                      href={doc.imageSrc}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline-primary"
                                    >
                                      <RiEyeLine /> View
                                    </a>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() =>
                                        removeExistingAdditionalFile(
                                          doc.visaApplicantDocumentId,
                                          index
                                        )
                                      }
                                    >
                                      <RiCloseLine /> Remove
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {newAdditionalDocs.length > 0 && (
                          <div>
                            <h6 className="mb-3">New Documents to Upload</h6>
                            <div className="row">
                              {newAdditionalDocs.map((file, index) => (
                                <div
                                  key={index}
                                  className="col-md-6 col-sm-6 mb-3"
                                >
                                  <div className="card document-card">
                                    <div className="card-body text-center p-2">
                                      {renderAdditionalDocPreview(
                                        newAdditionalDocPreviews[index]
                                          ?.preview,
                                        getDocumentType(file),
                                        `Document ${index + 1}`
                                      )}
                                      <h6 className="document-title mb-1">
                                        {getDocumentType(file) === "pdf" ? (
                                          <RiFilePdf2Line className="text-danger me-1" />
                                        ) : (
                                          <RiFileTextLine className="text-primary me-1" />
                                        )}
                                        {file.name.length > 15
                                          ? `${file.name.substring(0, 12)}...`
                                          : file.name}
                                      </h6>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() =>
                                          removeNewAdditionalFile(index)
                                        }
                                      >
                                        <RiCloseLine /> Remove
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card mb-4 shadow-sm h-100">
                      <div className="card-header bg-primary">
                        <h5 className="mb-0 text-white">
                          Additional Information
                        </h5>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <select
                            name="visaApplicantStatus"
                            value={values.visaApplicantStatus}
                            onChange={handleInputChange}
                            className="form-select"
                          >
                            <option value="PENDING">Pending</option>
                            <option value="APPROVED">Approved</option>
                            <option value="REJECTED">Rejected</option>
                            <option value="UNDER_REVIEW">Under Review</option>
                          </select>
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Comment</label>
                          <textarea
                            name="comments"
                            value={values.comments}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="4"
                            maxLength={500}
                            placeholder="Add any additional comments or notes here..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleClose} disabled={btnLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={btnLoading}
          >
            {btnLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                {recordForEdit ? "Updating..." : "Adding..."}
              </>
            ) : recordForEdit ? (
              "Update Applicant"
            ) : (
              "Add Applicant"
            )}
          </Button>
        </Modal.Footer>

        <style jsx>{`
          .applicant-item {
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .applicant-item:hover {
            background-color: #e9ecef !important;
          }

          .cursor-pointer {
            cursor: pointer;
          }

          .document-card {
            transition: transform 0.2s;
          }

          .document-card:hover {
            transform: translateY(-2px);
          }

          .document-title {
            font-size: 0.8rem;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        `}</style>
      </Modal>

      {/* Document Preview Modals */}
      <Modal
        show={showProfilePreview}
        onHide={() => setShowProfilePreview(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Profile Photo Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewTypes.profileSrc === "pdf" ? (
            <div className="p-4">
              <RiFilePdf2Line className="text-danger" size={80} />
              <p className="mt-3">PDF Document</p>
            </div>
          ) : (
            <img
              src={previews.profileSrc}
              alt="Profile Preview"
              className="img-fluid"
              style={{ maxHeight: "500px" }}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showPassportFrontPreview}
        onHide={() => setShowPassportFrontPreview(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Passport Front Page Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewTypes.passportFrontSrc === "pdf" ? (
            <div className="p-4">
              <RiFilePdf2Line className="text-danger" size={80} />
              <p className="mt-3">PDF Document</p>
            </div>
          ) : (
            <img
              src={previews.passportFrontSrc}
              alt="Passport Front Preview"
              className="img-fluid"
              style={{ maxHeight: "500px" }}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showPassportBackPreview}
        onHide={() => setShowPassportBackPreview(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Passport Back Page Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewTypes.passportBackSrc === "pdf" ? (
            <div className="p-4">
              <RiFilePdf2Line className="text-danger" size={80} />
              <p className="mt-3">PDF Document</p>
            </div>
          ) : (
            <img
              src={previews.passportBackSrc}
              alt="Passport Back Preview"
              className="img-fluid"
              style={{ maxHeight: "500px" }}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <RiDeleteBinLine size={48} className="text-danger mb-3" />
            <h5>Are you sure you want to delete this applicant?</h5>
            <p className="text-muted">
              This will permanently delete{" "}
              <strong>
                {applicantToDelete?.firstName} {applicantToDelete?.lastName}
              </strong>{" "}
              and all associated documents.
            </p>
            <p className="text-danger">
              <small>This action cannot be undone.</small>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={handleDeleteApplicant}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                Deleting...
              </>
            ) : (
              <>
                <RiDeleteBinLine className="me-2" />
                Delete Applicant
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ApplicantForm;
